/**
 * E2E Encryption utilities using Web Crypto API
 * - ECDH P-256 for key exchange
 * - AES-GCM 256-bit for message encryption
 * - PBKDF2 (600K iterations) for PIN-based key wrapping
 */

const PBKDF2_ITERATIONS = 600000;
const ECDH_CURVE = 'P-256';

// =========== Key Generation ===========

export async function generateKeyPair() {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: ECDH_CURVE },
    true,
    ['deriveKey', 'deriveBits']
  );
  return keyPair;
}

export async function exportPublicKey(publicKey) {
  const jwk = await crypto.subtle.exportKey('jwk', publicKey);
  return JSON.stringify(jwk);
}

export async function importPublicKey(jwkString) {
  const jwk = JSON.parse(jwkString);
  return crypto.subtle.importKey(
    'jwk', jwk,
    { name: 'ECDH', namedCurve: ECDH_CURVE },
    true, []
  );
}

async function exportPrivateKey(privateKey) {
  const jwk = await crypto.subtle.exportKey('jwk', privateKey);
  return JSON.stringify(jwk);
}

async function importPrivateKey(jwkString) {
  const jwk = JSON.parse(jwkString);
  return crypto.subtle.importKey(
    'jwk', jwk,
    { name: 'ECDH', namedCurve: ECDH_CURVE },
    true, ['deriveKey', 'deriveBits']
  );
}

// =========== PIN-based Key Wrapping ===========

function buf2b64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function b642buf(b64) {
  const str = atob(b64);
  const buf = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) buf[i] = str.charCodeAt(i);
  return buf;
}

async function deriveKeyFromPin(pin, salt) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(pin), 'PBKDF2', false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false, ['encrypt', 'decrypt']
  );
}

export async function encryptPrivateKeyWithPin(privateKey, pin) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aesKey = await deriveKeyFromPin(pin, salt);
  const rawPrivateKey = new TextEncoder().encode(await exportPrivateKey(privateKey));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey, rawPrivateKey
  );
  return {
    encrypted_private_key: buf2b64(ciphertext),
    salt: buf2b64(salt),
    iv: buf2b64(iv)
  };
}

export async function decryptPrivateKeyWithPin(encryptedData, pin) {
  const { encrypted_private_key, salt, iv } = encryptedData;
  const saltBuf = b642buf(salt);
  const ivBuf = b642buf(iv);
  const ciphertextBuf = b642buf(encrypted_private_key);
  const aesKey = await deriveKeyFromPin(pin, saltBuf);
  try {
    const rawKey = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBuf },
      aesKey, ciphertextBuf
    );
    const jwkString = new TextDecoder().decode(rawKey);
    return importPrivateKey(jwkString);
  } catch {
    throw new Error('Wrong PIN');
  }
}

// =========== Message Encryption/Decryption ===========

async function deriveSharedKey(privateKey, publicKey) {
  const bits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: publicKey },
    privateKey, 256
  );
  return crypto.subtle.importKey(
    'raw', bits, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']
  );
}

export async function encryptMessage(plaintext, senderPrivateKey, recipientPublicKey) {
  const sharedKey = await deriveSharedKey(senderPrivateKey, recipientPublicKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, sharedKey, encoded
  );
  return JSON.stringify({ ct: buf2b64(ciphertext), iv: buf2b64(iv) });
}

export async function decryptMessage(encryptedContent, recipientPrivateKey, senderPublicKey) {
  try {
    const { ct, iv } = JSON.parse(encryptedContent);
    const sharedKey = await deriveSharedKey(recipientPrivateKey, senderPublicKey);
    const ciphertextBuf = b642buf(ct);
    const ivBuf = b642buf(iv);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBuf }, sharedKey, ciphertextBuf
    );
    return new TextDecoder().decode(decrypted);
  } catch {
    return null;
  }
}
